import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resizeImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Handshake, ExternalLink, Plus, Trash2, Loader2, ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";

export default function Convenzioni() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ titolo: "", descrizione: "", link: "", foto: null });

  const { data: convenzioni = [] } = useQuery({
    queryKey: ["convenzioni"],
    queryFn: async () => (await api.get("/public/convenzioni")).data,
  });

  const onFoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const dataUrl = await resizeImage(f, 800, 600, 0.85); setForm((s) => ({ ...s, foto: dataUrl })); }
    catch { toast.error("Errore caricamento foto"); }
  };

  const submit = async () => {
    if (!form.titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/convenzioni", form);
      toast.success("Convenzione aggiunta");
      qc.invalidateQueries({ queryKey: ["convenzioni"] });
      setOpen(false);
      setForm({ titolo: "", descrizione: "", link: "", foto: null });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/convenzioni/${id}`); qc.invalidateQueries({ queryKey: ["convenzioni"] }); toast.success("Convenzione eliminata"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#14382C] text-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-5 sm:px-6">
          <img src="/emblem.png" alt="Emblema" className="h-12 w-auto object-contain" />
          <div className="flex-1">
            <h1 className="font-heading text-lg font-extrabold leading-tight sm:text-xl">Convenzioni CRAL ANGE</h1>
            <p className="text-xs text-emerald-100/80">Associazione Nazionale Guardie Ecozoofile ODV</p>
          </div>
          {!user && (
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-emerald-100 hover:text-white" data-testid="back-login">
              <ArrowLeft className="h-4 w-4" /> Accedi
            </Link>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Le convenzioni e gli accordi riservati ai soci.</p>
          {isManager && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-[#D97706] hover:bg-[#b45309]" data-testid="new-convenzione-button"><Plus className="mr-2 h-4 w-4" /> Aggiungi convenzione</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuova convenzione</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="flex items-center gap-4 rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <div className="flex h-20 w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-300 bg-white">
                      {form.foto ? <img src={form.foto} alt="Anteprima" className="h-full w-full object-cover" /> : <Camera className="h-6 w-6 text-slate-300" />}
                    </div>
                    <div>
                      <Label>Foto / logo</Label>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFoto} data-testid="convenzione-foto-input" />
                      <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => fileRef.current?.click()} data-testid="convenzione-foto-upload"><Camera className="mr-2 h-4 w-4" /> Carica foto</Button>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Titolo</Label><Input data-testid="convenzione-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Descrizione</Label><Textarea rows={3} data-testid="convenzione-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Link alla convenzione</Label><Input data-testid="convenzione-link" placeholder="https://..." value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button onClick={submit} disabled={saving} data-testid="save-convenzione-button" className="bg-[#14382C] hover:bg-[#0E281F]">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {convenzioni.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-400">Nessuna convenzione disponibile al momento.</div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {convenzioni.map((c) => (
              <div key={c.id} className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md" data-testid={`convenzione-${c.id}`}>
                <div className="flex h-40 items-center justify-center overflow-hidden bg-slate-100">
                  {c.foto ? <img src={c.foto} alt={c.titolo} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <Handshake className="h-10 w-10 text-slate-300" />}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading font-bold text-slate-900">{c.titolo}</h3>
                  {c.descrizione && <p className="mt-1 flex-1 text-sm text-slate-500">{c.descrizione}</p>}
                  <div className="mt-3 flex items-center gap-2">
                    {c.link && <a href={c.link} target="_blank" rel="noopener noreferrer" data-testid={`convenzione-link-${c.id}`} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#166534] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#14532d]"><ExternalLink className="h-4 w-4" /> Vai alla convenzione</a>}
                    {isManager && <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-convenzione-${c.id}`} onClick={() => remove(c.id)}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
